// DsField — implementa a assinatura de formulário do canvas (DESIGN-SYSTEM.md §6.3):
//   `.fieldlab` / `.flabel` em 18 artboards -> o rótulo
//   `.fieldbox`             em  7 artboards -> density="md"
//   `.fld` / `.fld.dim`     (portal)        -> density="sm"
//   `.ferr`                                 -> a prop `error`
//
// Rótulo EXTERNO acima do campo: não existe label flutuante em nenhum dos 143 artboards.
// `DsField` é wrapper de layout — renderiza o rótulo e aceita qualquer controle filho.
// Quando o filho for `TextField`, passar `label=""`, `InputProps={{ notched: false }}` e
// `SX_FIELD_CANVAS`.
//
// O `MuiTextField` do tema NÃO é tocado: isso mudaria todos os formulários do sistema e
// colide com `docs_new/ADR-FORM-LAYOUT.md`. O rótulo externo entra por componente, tela a tela.
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA
//
// 1) §3.9 — `SX_FIELD_CANVAS` não compõe. Pedido por 4 LOTES (L08, L21, L22, L25).
//    Estava declarado como `SxProps<Theme>`, união que já admite array e função; o MUI
//    não aceita `sx` aninhado, então toda view que queria somar um `sx` local precisava
//    de `as SystemStyleObject<Theme>` (L08, L22, L25) ou reescrevia os literais à mão
//    (L21 — ver `acao-fiscal/views/consultar-acoes-fiscais-view.tsx:386-397`, que anota
//    "escritos aqui porque o `sx` do `TextField` não aceita a forma em array do preset").
//    Agora o tipo é `SystemStyleObject<Theme>`: `{ ...SX_FIELD_CANVAS, ...local }` passa a
//    compilar. A mudança é ADITIVA — `SystemStyleObject<Theme>` é membro de `SxProps<Theme>`,
//    então todo `sx={SX_FIELD_CANVAS}` que já existe continua válido, e os `as
//    SystemStyleObject<Theme>` das views viram asserções redundantes (inofensivas).
//
// 2) §3.15 — borda vermelha do estado de erro. Pedido por L15 (5) e L20.
//    O relatório diz que `'& fieldset': { borderColor: 'divider' }` "apaga a borda vermelha".
//    CONFERIDO E NÃO REPRODUZIDO na configuração de hoje: a regra do preset sai como
//    `.css-x fieldset`, especificidade (0,1,1), enquanto o override de erro do tema
//    (`theme/core/components/textfield.tsx:57-61`) sai como
//    `.css-y.Mui-error .MuiOutlinedInput-notchedOutline`, (0,3,0) — e a do próprio MUI
//    (`@mui/material@7.0.1/OutlinedInput/OutlinedInput.js:78-80`) é igual. (0,3,0) vence
//    (0,1,1) e a borda de erro continua vermelha.
//    O que É real e entra aqui, por isso, são DOIS pontos:
//      a. o preset não declarava exceção nenhuma, então bastava um consumidor subir a
//         especificidade (`'&&& fieldset'`, ou espalhar o preset dentro de outro seletor)
//         para apagar de verdade a borda de erro. A exceção abaixo trava isso por escrito,
//         com (0,3,1) — acima do tema — e deixa `Mui-focused`/`Mui-disabled` intactos, que
//         continuam vindo do tema.
//      b. `DsField` recebia `error` e só desenhava a mensagem `.ferr`: o campo em si só
//         ficava vermelho se a view também passasse `error` ao `TextField` filho. Agora a
//         prop `error` DERIVA a pele do campo (era o pedido de L20, "derivação de erro").
//    Valor do artboard, não inventado: `.fld.err{border-color:var(--error-main);
//    background:rgba(255,86,48,0.06)}` — família `atendimento` (12 artboards), sem
//    override no dark (só `.ferr` vira `--error-light`, que este arquivo já fazia).

import type { SystemStyleObject } from '@mui/system';
import type { Theme, SxProps } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';

import { DS_BOX_RADIUS } from './tokens';

// ----------------------------------------------------------------------

export type DsFieldProps = {
  label: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode;
  children: React.ReactNode;
  /** Densidade do campo. Padrão: 'md' (`.fieldbox`). 'sm' é a forma do portal (`.fld`). */
  density?: 'md' | 'sm';
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

/**
 * `.fieldbox` aplicado a um `TextField` já existente — o `fieldset` do outlined perde o
 * recorte da legenda porque o rótulo do canvas vive fora do campo.
 *
 * A borda `divider` vale para o campo em repouso. `Mui-error` tem exceção explícita
 * (`.fld.err` do canvas); `Mui-focused` e `Mui-disabled` seguem vindo do tema.
 * Os dois seletores da exceção cobrem as duas formas de uso do preset: espalhado num
 * invólucro/`TextField` (`& …`) e aplicado direto no `InputProps` do outlined (`&…`).
 */
const FIELD_CANVAS = {
  '& .MuiOutlinedInput-root': { borderRadius: `${DS_BOX_RADIUS}px`, fontSize: 13 },
  '& .MuiOutlinedInput-input': { padding: '9px 12px' },
  '& fieldset': { borderColor: 'divider', top: 0, '& legend': { display: 'none' } },
  '& .MuiOutlinedInput-root.Mui-error fieldset, &.MuiOutlinedInput-root.Mui-error fieldset': {
    borderColor: 'error.main',
  },
} satisfies SystemStyleObject<Theme>;

/**
 * Preset composável: `sx={SX_FIELD_CANVAS}` e também
 * `sx={{ ...SX_FIELD_CANVAS, minWidth: 200 }}` (§3.9).
 */
export const SX_FIELD_CANVAS: SystemStyleObject<Theme> = FIELD_CANVAS;

/** `.fieldlab` / `.flabel`: 11px/700 secundário, bloco, com 6px até o campo. */
const SX_LABEL = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.5,
  color: 'text.secondary',
  mb: '6px',
} satisfies SystemStyleObject<Theme>;

/** `.fieldbox`: caixa de 1px, raio 8, 13px sobre `background.paper`. */
const SX_DENSITY_MD = {
  ...FIELD_CANVAS,
  '& .MuiOutlinedInput-root': {
    ...FIELD_CANVAS['& .MuiOutlinedInput-root'],
    color: 'text.primary',
    bgcolor: 'background.paper',
    minWidth: 0,
    '&.Mui-disabled': { bgcolor: 'background.neutral', color: 'text.disabled' },
  },
} satisfies SystemStyleObject<Theme>;

/** `.fld` do portal: 12px/600 sobre `background.default`; `.dim` é o placeholder. */
const SX_DENSITY_SM = {
  ...FIELD_CANVAS,
  '& .MuiOutlinedInput-root': {
    ...FIELD_CANVAS['& .MuiOutlinedInput-root'],
    fontSize: 12,
    fontWeight: 600,
    color: 'text.primary',
    bgcolor: 'background.default',
    minWidth: 0,
    '&.Mui-disabled': { bgcolor: 'background.neutral', color: 'text.disabled' },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    fontSize: 12,
    fontWeight: 500,
    color: 'text.secondary',
    opacity: 1,
  },
} satisfies SystemStyleObject<Theme>;

/**
 * `.ferr`: 11px/600 `error.main`, 6px acima e **sem recuo lateral** — o `FormHelperText`
 * do MUI usa 12px/400 com `margin:3px 14px 0`.
 */
const SX_ERROR = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.5,
  color: 'error.main',
  mt: '6px',
} satisfies SystemStyleObject<Theme>;

const sxErrorDark = (theme: Theme) =>
  theme.applyStyles('dark', { color: theme.vars.palette.error.light });

/**
 * `.fld.err` do canvas: `border-color:var(--error-main)` + `background:rgba(255,86,48,0.06)`,
 * escrito por token. Aplicado quando a prop `error` tem conteúdo, para o campo ficar vermelho
 * mesmo quando a view só passou a mensagem ao `DsField` e não `error` ao controle filho.
 *
 * Especificidade proposital: a borda sai em (0,2,1), acima do `& fieldset` (0,1,1) do preset
 * e abaixo do `Mui-error` do tema (0,3,0) — que pinta a MESMA cor quando o controle também
 * está em erro, então os dois caminhos convergem. O canvas não troca esses valores no dark.
 */
const sxCampoErro = (theme: Theme): SystemStyleObject<Theme> => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.06),
  },
  '& .MuiOutlinedInput-root fieldset': { borderColor: theme.vars.palette.error.main },
});

// ----------------------------------------------------------------------

export function DsField({ label, required, error, children, density = 'md', sx }: DsFieldProps) {
  return (
    <Box
      component="label"
      sx={[
        { display: 'block', minWidth: 0 },
        density === 'md' ? SX_DENSITY_MD : SX_DENSITY_SM,
        Boolean(error) && sxCampoErro,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box component="span" sx={SX_LABEL}>
        {label}
        {required ? ' *' : null}
      </Box>

      {children}

      {error ? (
        <Box component="span" sx={[SX_ERROR, sxErrorDark]}>
          {error}
        </Box>
      ) : null}
    </Box>
  );
}
